import { Component, OnInit } from '@angular/core';
import { JsonService } from '../_services/json.service';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import { ConfirmationDialogService } from '../_services/confirmation-dialog.service';
import { DialogService } from '../dialog/dialog.service';
import { TranslateService } from '@ngx-translate/core';
import { StringSwitchService } from '../_helpers/string-switch.service';
import { LoadingService } from '../_services/loading.service';
import { LoggerService } from '../_services/logger.service';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';

@Component({
  selector: 'app-directory-list',
  templateUrl: './directory-list.component.html',
  styleUrl: './directory-list.component.scss'
})
export class DirectoryListComponent implements OnInit {
  public jsonPaises: Array<any> = [];
  public listaRegiao: Array<any> = [];
  public listaGrupos: Array<any> = [];
  public listaItens: Array<any> = [];
  public listaShowItens: Array<any> = [];
  public currentPage = 1;
  public pageSize = 15;
  public loading: boolean = false;
  public totalItems = 0;
  public acesso: string = 'operador';
  public pathSelected: string = '';
  public countrySelected: string = '';
  public regiaoSelecionada: string = '';

  public filtro: any = {
    status: 'W',
    usuario: '',
    data: '',
    pais: '',
    cidade: '',
    pdv: '',
    ano: '',
    mes: '',
    regiao: '',
    provedor: '',
    envio: '',
    grupos: '',
    selecionarTodos: '',
    responsavel: '',
    filename: ''
  };

  constructor(
    private jsonService: JsonService,
    private route: ActivatedRoute,
    private location: Location,
    private confirmationDialog: ConfirmationDialogService,
    private dialogService: DialogService,
    private router: Router,
    private logger: LoggerService,
    private loadingService: LoadingService,
    private stringSwitchService: StringSwitchService,
    private translate: TranslateService
  ) { }

  ngOnInit(): void {
    this.loadDiretorios();

    this.jsonService.listCountry().subscribe(
      resp => {
        this.jsonPaises = resp;
      },
      erro => {
        this.logger.error('Erro ao carregar países', erro);
      }
    );

    this.jsonService.getRegiao().subscribe(
      resp => {
        this.listaRegiao = resp;
      },
      erro => {
        this.logger.error('Erro ao carregar regiões', erro);
      }
    );
  }

  loadDiretorios(filtra: boolean = false) {
    this.jsonService.getDirectoryList().subscribe(
      resp => {
        this.listaItens = resp.map(item => ({ ...item, totalPdvs: null }));
        this.loadPdvCounts(filtra);
      },
      erro => {
        this.logger.error('Erro ao carregar diretórios', erro);
      }
    );
  }

  private loadPdvCounts(filtra: boolean = false) {
    const countryIds = Array.from(new Set(
      this.listaItens
        .filter(item => item.isLimitPdvs && item.countryId)
        .map(item => item.countryId)
    ));

    if (!countryIds.length) {
      this.refreshList(filtra);
      return;
    }

    forkJoin(
      countryIds.map(countryId =>
        this.jsonService.getCountPdvs(countryId).pipe(
          catchError(erro => {
            this.logger.error('Erro ao carregar quantidade de PDVs', erro);
            return of(null);
          })
        )
      )
    ).subscribe(counts => {
      const countByCountry = new Map<string, number | null>();
      countryIds.forEach((countryId, index) => countByCountry.set(countryId, counts[index]));

      this.listaItens = this.listaItens.map(item => ({
        ...item,
        totalPdvs: item.isLimitPdvs ? countByCountry.get(item.countryId) ?? null : null
      }));

      this.refreshList(filtra);
    });
  }

  private refreshList(filtra: boolean = false) {
    if (filtra) {
      this.atualizaFiltro();
    } else {
      this.listaShowItens = this.listaItens;
    }
  }

  editarDiretorio(item: any) {
    this.router.navigate(['/diretorio-detalhe', item.id]);
  }

  adicionarDiretorio() {
    this.router.navigate(['/diretorio-detalhe', 'novo'], { state: { directories: this.listaItens } });
  }

  async removeGroup(item: any) {
    let title = this.translate.instant('Êxito') + '!';
    let texto = this.translate.instant('Tem certeza de que deseja deletar esse diretório?');
    const confirmed = await this.confirmationDialog.confirm(title, texto);

    if (confirmed) {
      const formData = new FormData();
      formData.append('id', item.id);
      formData.append('_method', 'DELETE');
      this.jsonService.deleteDirectory(formData).subscribe(
        resp => {
          let title = this.translate.instant('Êxito') + '!';
          let texto = this.translate.instant('Diretório removido com sucesso!');
          this.dialogService.abrirDialog('aviso', title, texto);
          this.loadDiretorios();
        },
        erro => {
          this.logger.error('Erro ao remover diretório', erro);
          let title = this.translate.instant('Atenção') + '!';
          let texto = this.translate.instant('Não foi possível remover o diretório, #error desconhecido#');
          this.dialogService.abrirDialog('aviso', title, texto);
        }
      );
    }
  }

  atualizaFiltro() {
    if (this.filtro.pais != '') {
      this.listaShowItens = this.listaItens.filter(item => item.countryId == this.filtro.pais.trim());
    }
    if (this.filtro.regiao != '') {
      this.listaShowItens = this.listaItens.filter(item => item.indexRegion == this.filtro.regiao.trim());
    }
    if (this.filtro.search) {
      this.listaShowItens = this.listaShowItens.filter(item => item.rootPath.toLowerCase().includes(this.filtro.search.toLowerCase()));
    }
    if (!this.filtro.regiao && !this.filtro.search && !this.filtro.pais) {
      this.limparFiltro();
    }
  }

  public getCountry(id: string): any {
    let message = this.translate.instant('País não encontrado');
    const country = this.jsonPaises.find(pais => pais.id === id);
    return country ? country.name : message;
  }

  getRegionName(regionCode: string): string {
    let message = this.translate.instant('Região não encontrada');
    const region = this.listaRegiao.find(regiao => regiao.regionCode == regionCode);
    return region ? region.name : message;
  }

  limparFiltro() {
    this.filtro = {
      pais: '',
      regiao: '',
      search: ''
    };
    this.listaShowItens = this.listaItens;
  }
}
